import type { Software } from "@/components/Combobox/SoftwareSelectorCombobox";
import { VStack } from "@/components/Layout";
import { SoftwareSelect } from "@/components/Select/SoftwareSelect";
import { Text } from "@/components/Typography/Text";
import { Command, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/cn";
import { useActorRef } from "@xstate/react";
import { CommandInput } from "cmdk";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import {
  type SearchSoftwareOption,
  createSoftwareSelectMachine,
} from "../-xstate/software-select-machine";
import type { Attribute, FieldRenderInputProps } from "../../types";
import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";
import { ReadOnlyField } from "./ReadOnlyField";

/**
 * Softwareを検索するフィールドコンポーネント
 * Softwareを検索するフィールドの入力を提供します
 */
export const SoftwareSelectField = memo(
  ({
    fieldId,
    field,
    disabled,
    readOnly,
    layout,
    className,
    "data-testid": testId,
  }: BaseFieldProps) => {
    const renderInput = useCallback(
      ({
        value: inputValue,
        onChange: handleChange,
        placeholder,
        disabled: isDisabled,
      }: FieldRenderInputProps) => {
        const machine = useMemo(() => createSoftwareSelectMachine(), []);
        const stateMachineService = useActorRef(machine, {
          input: {
            selectedSoftwareId: inputValue as string,
          },
        });

        useEffect(() => {
          stateMachineService.send({ type: "INIT" });
        }, [stateMachineService]);
        // // useSyncExternalStoreを使用して状態を一括取得
        const state = useSyncExternalStore(
          (callback) => {
            const subscription = stateMachineService.subscribe(callback);
            return () => subscription.unsubscribe();
          },
          () => stateMachineService.getSnapshot(),
        );

        if (!field) {
          return <div></div>;
        }

        const context = state.context;
        const selectedSoftwareOption = context.selectedSoftwareOption;
        const searchText = context.searchText;
        const searchResults = context.searchResults;

        const handleSoftwareSelect = (
          softwareId: string,
          option?: SearchSoftwareOption,
        ) => {
          if (option) {
            stateMachineService.send({
              type: "SELECT_SOFTWARE",
              softwareId,
              softwareOption: option,
            });
            handleChange(option.id);
          }
        };

        const handleSoftwareRemove = (softwareId: string) => {
          stateMachineService.send({ type: "REMOVE_SOFTWARE", softwareId });
          handleChange(undefined);
        };

        const handleSearchChange = (text: string) => {
          stateMachineService.send({ type: "SEARCH_SOFTWARE", text });
        };

        return (
          <VStack className="gap-2">
            <SoftwareSelection
              options={searchText ? searchResults : []}
              value={selectedSoftwareOption?.id ?? null}
              selectedSoftwareOption={selectedSoftwareOption}
              searchText={searchText}
              onSearchChange={handleSearchChange}
              onValueChange={handleSoftwareSelect}
              onRemoveSoftware={handleSoftwareRemove}
              disabled={isDisabled}
              readonly={readOnly || field.readonly}
              field={field}
              placeholder={placeholder}
            />
          </VStack>
        );
      },
      [field, readOnly],
    );
    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        disabled={disabled}
        readOnly={false} //内部で切り替えるので
        layout={layout}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

function SoftwareSelection(props: {
  options: SearchSoftwareOption[];
  value: string | null;
  selectedSoftwareOption: SearchSoftwareOption | null;
  searchText: string;
  onSearchChange?: (text: string) => void;
  onValueChange: (value: string, option?: SearchSoftwareOption) => void;
  onRemoveSoftware?: (softwareId: string) => void;
  disabled?: boolean;
  readonly?: boolean;
  placeholder?: string;
  field: Attribute;
}) {
  // NOTE: 検索テキストの選択後にクリアするために保持
  const [searchText, setSearchText] = useState("");

  const handleSearchChange = useCallback(
    (text: string) => {
      props.onSearchChange?.(text);
      setSearchText(text);
    },
    [props.onSearchChange],
  );

  const handleOnSelect = useCallback(
    (value: string, overrideOption?: SearchSoftwareOption) => {
      const option =
        overrideOption ?? props.options.find((opt) => opt.value === value);
      if (option && !option.disabled) {
        props.onValueChange(value, option);
        setSearchText("");
      }
    },
    [props.onValueChange, props.options],
  );

  const convertSoftwareToSearchOption = useCallback(
    (software: Software): SearchSoftwareOption => {
      const softwareId = software.id ?? software.appIntegrationId ?? "";
      return {
        id: softwareId,
        value: softwareId,
        label: software.name,
        description: undefined,
      };
    },
    [],
  );
  return (
    <>
      {props.readonly ? (
        <ReadOnlyField
          value={`${props.selectedSoftwareOption?.label} ${
            props.selectedSoftwareOption?.description
              ? `(${props.selectedSoftwareOption?.description})`
              : ""
          }`}
          field={props.field}
        />
      ) : (
        <Command shouldFilter={false}>
          <div className="flex min-h-[80px] gap-2 rounded-md border border-b border-neutral-muted-border px-3 py-2 shadow-sm">
            <div className="flex flex-1 flex-wrap items-start gap-1 py-2">
              {props.selectedSoftwareOption ? (
                <SoftwareSelect
                  onChange={(software) => {
                    if (!software) return;
                    const option = convertSoftwareToSearchOption(software);
                    handleOnSelect(option.id, option);
                  }}
                  allowEmpty={false}
                  placeHolder="ソフトウェアを選択"
                  disabledSoftwareIds={
                    new Set(
                      props.options
                        .map((s) => s.id)
                        .filter((id): id is string => !!id),
                    )
                  }
                />
              ) : (
                <CommandInput
                  className="h-6 w-full text-xs outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={props.placeholder}
                  value={searchText}
                  onValueChange={handleSearchChange}
                  disabled={props.disabled}
                />
              )}
            </div>
          </div>
          {props.options.length ? (
            <CommandList
              className={cn(
                "gap-1 rounded-md border border-neutral-muted-border bg-neutral-fill p-1 shadow-sm",
              )}
            >
              {props.options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.value}
                  onSelect={
                    option.disabled
                      ? undefined
                      : () => handleOnSelect(option.value, option)
                  }
                  className={cn(
                    "cursor-pointer gap-1",
                    option.disabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <div className="flex flex-col">
                    <Text variant="labelSmall">{option.label}</Text>
                    {option.description ? (
                      <Text variant="pSmall" className="text-muted-foreground">
                        {option.description}
                      </Text>
                    ) : null}
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          ) : (
            <></>
          )}
        </Command>
      )}
    </>
  );
}

SoftwareSelectField.displayName = "SoftwareSelectField";
